<template>
  <div class="flex items-center" :class="containerClass">
    <div class="mr-1.5 employee-status-container" :class="avatarContainerClass">
      <img
        v-if="user.profileImage || user.systemUserId?.profileImage"
        :src="getEmployeeImage(user)"
        @error="handleImageError"
        :alt="altText || 'User Image'"
        class="avtarSize"
        :style="getImageStyle()"
      />
      <v-avatar
        v-else
        :color="getAvatarColor(getUserFullName(user))"
        :size="size"
        class="avtarSize"
        :style="getAvatarStyle()"
      >
        <span class="white--text font-weight-medium" :class="initialsClass">
          {{ getUserInitials(getUserFullName(user)) }}
        </span>
      </v-avatar>
    </div>
    <div
      v-if="showName"
      class="w-full flex items-center justify-between"
      :class="nameContainerClass"
    >
      <div class="flex flex-col truncate flex-grow">
        <span
          v-if="user"
          class="truncate-name flex items-center font-semibold"
          :class="nameClass"
          v-tooltip="tooltipEnabled ? { text: getUserFullName(user) } : undefined"
        >
          {{ getUserFullName(user) }}
          <span v-if="showEmployeeId && user?.employmentDetails?.empId" class="ml-1">
            / {{ user?.employmentDetails?.empId || user?.employeeId }}</span
          >
          <slot name="name-content"></slot>
          <span
            v-if="user?.systemUserId?.isOwner && showOwnerTag"
            class="text-[12px] rounded-xl px-2 ml-2"
            :class="{
              'text-green-600 bg-green-100': !$vuetify.theme.dark,
              'text-white': $vuetify.theme.dark
            }"
            :style="{
              backgroundColor: $vuetify.theme.dark ? $vuetify.theme.currentTheme.secondary : ''
            }"
          >
            Owner
          </span>
        </span>
        <div
          v-if="(user?.maskedMobileNumber || user?.maskedEmail) && showMaskDetails"
          class="text-xs text-gray-500 mt-0.5 flex items-center"
        >
          <span v-if="user?.maskedMobileNumber">{{ user.maskedMobileNumber }}</span>
          <span v-if="user?.maskedEmail" class="mx-1 font-semibold">/</span>
          <span v-if="user?.maskedEmail">{{ user.maskedEmail }}</span>
        </div>
        <slot name="additional-content"></slot>
      </div>

      <v-icon
        v-if="showInfoIcon"
        small
        color="grey lighten-1"
        class="ml-2 cursor-pointer info-hover-icon flex-shrink-0"
        v-tooltip="{
          text: getDetailsTooltip(user),
          html: true,
          width: '300px'
          // classes: 'employee-info-tooltip'
        }"
      >
        mdi-information-outline
      </v-icon>
    </div>
  </div>
</template>

<script>
import { fullName } from '@/utils/common'

export default {
  name: 'UserAvatar',
  props: {
    user: {
      type: Object,
      required: true
    },
    size: {
      type: [Number, String],
      default: 32
    },
    showName: {
      type: Boolean,
      default: true
    },
    showEmployeeId: {
      type: Boolean,
      default: false
    },
    tooltipEnabled: {
      type: Boolean,
      default: true
    },
    altText: {
      type: String,
      default: 'User Image'
    },
    containerClass: {
      type: String,
      default: ''
    },
    avatarContainerClass: {
      type: String,
      default: ''
    },
    showMaskDetails: {
      type: Boolean,
      default: true
    },
    avatarClass: {
      type: String,
      default: ''
    },
    avatarStyle: {
      type: Object,
      default: () => ({})
    },
    nameContainerClass: {
      type: String,
      default: ''
    },
    nameClass: {
      type: String,
      default: 'text-sm'
    },
    initialsClass: {
      type: String,
      default: 'text-xs'
    },
    showOwnerTag: {
      type: Boolean,
      default: true
    },
    showInfoIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_S3_BASE_URL,
      fallbackImage: require('~/assets/img/genericImage.png')
    }
  },
  methods: {
    getUserInitials(user) {
      if (!user) return ''
      try {
        return user
          .split(' ')
          .filter((word) => word)
          .map((n) => n[0])
          .join('')
          .toUpperCase()
      } catch (error) {
        console.error('Error in getUserInitials:', error)
        return ''
      }
    },
    getAvatarColor(user) {
      const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#607D8B']
      if (!user) return colors[0]
      const index = user.length % colors.length
      return colors[index]
    },
    getEmployeeImage(user) {
      if (user.profileImage || user.systemUserId?.profileImage) {
        return `${this.baseUrl}${user?.profileImage || user?.systemUserId?.profileImage}`
      } else {
        if (user.gender === 'M') {
          return require('~/assets/img/profileMale.png')
        } else if (user.gender === 'F') {
          return require('~/assets/img/profileFemale.png')
        } else {
          return require('~/assets/img/genericImage.png')
        }
      }
    },
    handleImageError(event) {
      event.target.src = this.fallbackImage
    },
    getUserFullName(user) {
      // console.log(user)
      if (user.name) {
        return user.name
      }
      return fullName(user)
    },
    getImageStyle() {
      return {
        ...this.avatarStyle,
        height: `${this.size}px`,
        width: `${this.size}px`,
        minWidth: `${this.size}px`
      }
    },
    getAvatarStyle() {
      return {
        ...this.avatarStyle,
        height: `${this.size}px`,
        width: `${this.size}px`,
        minWidth: `${this.size}px`
      }
    },
    getDetailsTooltip(user) {
      if (!user) return 'No information available'

      const sections = []
      const sysUser = user.systemUserId || {}

      // Contact Info
      const contact = []
      const mobile = user.mobile || sysUser.mobile || user.phoneNumber
      const email = user.email || sysUser.email
      if (mobile) contact.push(`<b>Mob:</b> ${mobile}`)
      if (email) contact.push(`<b>Email:</b> ${email}`)
      if (contact.length) {
        sections.push(
          `<div class="mb-2"><div class="font-bold border-b mb-1">Contact</div>${contact.join(
            '<br>'
          )}</div>`
        )
      }

      // Employment Info
      const employment = []
      const empId = user.employeeId || user.employmentDetails?.empId
      const desig =
        user.designation?.designationName || user.designationId?.name || user.designation
      const dept = user.department?.departmentName || user.departmentId?.name || user.department
      const role = user.wfmRole?.role || user.role?.name || user.wfmRole

      if (empId) employment.push(`<b>ID:</b> ${empId}`)
      if (desig && typeof desig === 'string') employment.push(`<b>Desig:</b> ${desig}`)
      if (dept && typeof dept === 'string') employment.push(`<b>Dept:</b> ${dept}`)
      if (role && typeof role === 'string') employment.push(`<b>Role:</b> ${role}`)

      if (employment.length) {
        sections.push(
          `<div class="mb-2"><div class="font-bold border-b mb-1">Employment</div>${employment.join(
            '<br>'
          )}</div>`
        )
      }

      // Address Info
      const address = []
      if (user.addressLine1) address.push(user.addressLine1)
      if (user.city) address.push(user.city)
      if (user.state) address.push(user.state)
      if (user.pinCode || user.pincode) address.push(user.pinCode || user.pincode)

      if (address.length) {
        sections.push(
          `<div class="mb-2"><div class="font-bold border-b mb-1">Address</div>${address.join(
            ', '
          )}</div>`
        )
      }

      if (sections.length === 0) return 'No detailed information available'

      return `<div class="text-xs p-1 text-left" style="color: white; line-height: 1.4;">${sections.join(
        ''
      )}</div>`
    }
  }
}
</script>

<style scoped>
.truncate-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employee-status-container {
  position: relative;
  display: inline-block;
}

.employee-image,
.employee-icon {
  white-space: nowrap;
  border-radius: 50%;
}

.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
}
.avtarSize {
  /* Allow avatarStyle prop to control sizing */
  border-radius: 50%;
}

.info-hover-icon:hover {
  color: var(--v-primary-base) !important;
}
</style>
